import { Anchor, Box, Button, Group, Image, Modal, Stack } from "@mantine/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";

import {
  fetchEligibleNotification,
  recordNotificationEvent,
} from "@utils/api-communication/RestApiCli";
import {
  selectCurrentVersion,
  selectProductId,
} from "@utils/redux-utils/selectors/productSelectors";
import { trackNotificationChoice } from "@store/ui/uiSlice";
import {
  EligibleNotification,
  NotificationAction,
  NotificationEventType,
} from "@/types/notificationTypes";
import {
  getLocalizedNotificationContent,
  getSafeExternalUrl,
  getSafeInternalUrl,
  getSafeNotificationImageUrl,
  isGloballyExcludedNotificationPath,
  isRenderableNotificationAction,
} from "./notificationUtils";

const DEFAULT_ACTION_LABEL_KEYS: Record<NotificationAction["type"], string> = {
  internal_link: "notifications.actionOpen",
  external_link: "notifications.actionOpen",
  acknowledge: "notifications.actionGotIt",
  snooze: "notifications.actionRemindLater",
};

const ENDPOINT_MISSING_STATUSES = [404, 405, 501];

function actionVariant(action: NotificationAction): "filled" | "default" | "light" {
  if (action.variant === "secondary") return "default";
  if (action.variant === "light") return "light";
  return "filled";
}

export default function NotificationHost() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const productId = useSelector(selectProductId);
  const headsetVersion = useSelector(selectCurrentVersion);
  const deliveredThisSession = useRef(false);
  const endpointUnavailable = useRef(false);
  const [notification, setNotification] = useState<EligibleNotification | null>(null);

  const emitEvent = useCallback(
    (notificationId: string, type: NotificationEventType, actionId?: string) => {
      dispatch(
        trackNotificationChoice({ type, notificationId, ...(actionId ? { actionId } : {}) }),
      );
      void recordNotificationEvent(notificationId, {
        type,
        ...(actionId ? { actionId } : {}),
      }).catch(() => undefined);
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      deliveredThisSession.current ||
      endpointUnavailable.current ||
      !productId ||
      !headsetVersion ||
      isGloballyExcludedNotificationPath(location.pathname)
    ) {
      return;
    }

    const controller = new AbortController();
    fetchEligibleNotification(
      { pathname: location.pathname, headsetVersion: String(headsetVersion) },
      controller.signal,
    )
      .then((eligibleNotification) => {
        if (!eligibleNotification || controller.signal.aborted || deliveredThisSession.current)
          return;
        deliveredThisSession.current = true;
        setNotification(eligibleNotification);
        emitEvent(eligibleNotification.id, "shown");
      })
      .catch((error) => {
        if (
          isAxiosError(error) &&
          ENDPOINT_MISSING_STATUSES.includes(error.response?.status ?? 0)
        ) {
          endpointUnavailable.current = true;
        }
      });

    return () => controller.abort();
  }, [emitEvent, headsetVersion, location.pathname, productId]);

  const content = useMemo(
    () =>
      getLocalizedNotificationContent(
        notification?.contentByLocale ?? {},
        i18n.resolvedLanguage || i18n.language,
      ),
    [i18n.language, i18n.resolvedLanguage, notification],
  );
  const imageUrl = getSafeNotificationImageUrl(notification?.imageUrl);
  const actions = (notification?.actions ?? []).filter(isRenderableNotificationAction).slice(0, 3);

  const emitAndClose = useCallback(
    (type: NotificationEventType, actionId?: string) => {
      if (!notification) return;
      setNotification(null);
      emitEvent(notification.id, type, actionId);
    },
    [emitEvent, notification],
  );

  const handleClose = useCallback(() => emitAndClose("dismissed"), [emitAndClose]);

  const handleAction = useCallback(
    (action: NotificationAction) => {
      if (!notification) return;

      if (action.type === "acknowledge") {
        emitAndClose("acknowledged", action.id);
        return;
      }
      if (action.type === "snooze") {
        emitAndClose("snoozed", action.id);
        return;
      }

      const url =
        action.type === "internal_link"
          ? getSafeInternalUrl(action.url)
          : getSafeExternalUrl(action.url);
      if (!url) return;

      setNotification(null);
      emitEvent(notification.id, "action_clicked", action.id);
      if (action.acknowledgeOnClick) {
        emitEvent(notification.id, "acknowledged");
      }

      if (action.type === "internal_link") navigate(url);
      else window.open(url, "_blank", "noopener,noreferrer");
    },
    [emitAndClose, emitEvent, navigate, notification],
  );

  const handleBodyLink = useCallback(
    (internalUrl: string) => {
      if (!notification) return;
      setNotification(null);
      emitEvent(notification.id, "action_clicked");
      navigate(internalUrl);
    },
    [emitEvent, navigate, notification],
  );

  if (!notification || !content?.title || !content.body) return null;

  return (
    <Modal
      opened
      onClose={handleClose}
      title={content.title}
      centered
      size="lg"
      aria-label={content.title}
    >
      <Stack>
        {imageUrl ? <Image src={imageUrl} alt={content.imageAlt || ""} radius="md" /> : null}
        <Box className="notification-markdown">
          <ReactMarkdown
            components={{
              img: () => null,
              a: ({ href, children }) => {
                const internalUrl = getSafeInternalUrl(href);
                if (internalUrl) {
                  return (
                    <Anchor
                      href={internalUrl}
                      onClick={(event) => {
                        event.preventDefault();
                        handleBodyLink(internalUrl);
                      }}
                    >
                      {children}
                    </Anchor>
                  );
                }
                const externalUrl = getSafeExternalUrl(href);
                return externalUrl ? (
                  <Anchor href={externalUrl} target="_blank" rel="noopener noreferrer">
                    {children}
                  </Anchor>
                ) : (
                  <>{children}</>
                );
              },
            }}
          >
            {content.body}
          </ReactMarkdown>
        </Box>
        <Group justify="flex-end" gap="sm">
          {actions.length === 0 ? (
            <Button onClick={() => emitAndClose("acknowledged")}>
              {t("notifications.actionGotIt")}
            </Button>
          ) : (
            actions.map((action) => (
              <Button
                key={action.id}
                variant={actionVariant(action)}
                onClick={() => handleAction(action)}
              >
                {content.actionLabels?.[action.id] || t(DEFAULT_ACTION_LABEL_KEYS[action.type])}
              </Button>
            ))
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
